import React, { useState, useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { Spinner } from "reactstrap"
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine, Cell, Legend,
} from "recharts"
import { getArticulosPareto } from "../api"
import { formatCurrency, formatNumber, formatPercent, claseABC } from "../helpers"

const TooltipContent = ({ active, payload }) => {
  if (!active || !payload || !payload[0]) return null
  const d = payload[0].payload
  const cfg = claseABC(d.clase)
  return (
    <div style={{ background: "#0f172a", color: "#fff", padding: "8px 12px", borderRadius: 8, fontSize: 12, maxWidth: 320 }}>
      <div style={{ fontWeight: 700 }}>#{d.rank} · {d.descripcion}</div>
      <div style={{ marginTop: 4 }}>
        Clase <span style={{ background: cfg.chartColor, color: "#fff", padding: "1px 6px", borderRadius: 4, fontWeight: 700 }}>{cfg.label}</span>
      </div>
      <div style={{ marginTop: 4 }}>{formatCurrency(d.importe_neto)}</div>
      <div style={{ color: "#94a3b8", fontSize: 11 }}>
        Aporte individual: {formatPercent(d.aporte_individual_pct)}
      </div>
      <div style={{ color: "#fbbf24", fontSize: 11 }}>
        Acumulado: {formatPercent(d.aporte_acumulado_pct)}
      </div>
    </div>
  )
}

TooltipContent.propTypes = { active: PropTypes.bool, payload: PropTypes.array }

const ParetoTab = ({ filtros }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [topN, setTopN] = useState(50)

  useEffect(() => {
    if (!filtros?.nitemisor) return
    let cancelled = false
    setLoading(true)
    setError("")
    getArticulosPareto(filtros)
      .then(res => { if (!cancelled) setData(res.data) })
      .catch(err => { if (!cancelled) setError(err.response?.data?.error || err.message || "No se pudo cargar el Pareto.") })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [filtros])

  const productos = data?.data || []
  const resumenClases = data?.resumen_clases || {}
  const inflexion = data?.puntos_inflexion || {}

  const datosGrafico = useMemo(() => {
    return productos.slice(0, topN).map(p => ({
      ...p,
      // Para mostrar etiquetas más cortas en el eje X
      label: `#${p.rank}`,
    }))
  }, [productos, topN])

  if (loading && !data) {
    return (
      <div style={{ background: "#fff", borderRadius: 14, padding: 80, textAlign: "center", color: "#64748b", border: "1px solid #e2e8f0" }}>
        <Spinner color="primary" /> <div className="mt-2">Calculando Pareto…</div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: "14px 18px", background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", borderRadius: 10 }}>
        <i className="mdi mdi-alert-circle me-2" />{error}
      </div>
    )
  }

  if (!data) return null

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Insight box */}
      {inflexion.insight && (
        <div style={{
          background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
          border: "1px solid #bfdbfe",
          borderRadius: 12, padding: "14px 18px",
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: 10, background: "#3b82f6", color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0,
          }}>
            <i className="mdi mdi-podium-gold" />
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "#1e40af" }}>
              Análisis Pareto 80/20
            </div>
            <div style={{ fontSize: 14, color: "#0f172a", fontWeight: 600, marginTop: 2 }}>
              {inflexion.insight}
            </div>
            {(inflexion.productos_para_80_pct || inflexion.productos_para_95_pct) && (
              <div style={{ fontSize: 12, color: "#1e40af", marginTop: 4 }}>
                {inflexion.productos_para_80_pct != null && (
                  <><strong>{formatNumber(inflexion.productos_para_80_pct)}</strong> productos generan el 80% del importe</>
                )}
                {inflexion.productos_para_95_pct != null && (
                  <> · <strong>{formatNumber(inflexion.productos_para_95_pct)}</strong> productos generan el 95%</>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cards de resumen ABC */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        {["A", "B", "C"].map(c => {
          const info = resumenClases[c]
          if (!info) return null
          const cfg = claseABC(c)
          return (
            <div key={c} style={{
              flex: "1 1 240px", background: "#fff",
              border: "1px solid #e2e8f0", borderLeft: `4px solid ${cfg.chartColor}`,
              borderRadius: 12, padding: "14px 16px",
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: cfg.color, textTransform: "uppercase", letterSpacing: 0.4 }}>
                Clase {c} · {cfg.desc}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 6 }}>
                <span style={{ fontSize: 24, fontWeight: 700, color: "#0f172a" }}>
                  {formatNumber(info.count)}
                </span>
                <span style={{ fontSize: 14, color: cfg.chartColor, fontWeight: 700 }}>
                  {formatPercent(info.porcentaje_aporte)}
                </span>
              </div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                {formatCurrency(info.importe_total)}
              </div>
            </div>
          )
        })}
      </div>

      {/* Gráfico Pareto */}
      <div style={{ background: "#fff", borderRadius: 14, padding: 20, border: "1px solid #e2e8f0" }}>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
            <i className="mdi mdi-chart-bar-stacked me-2 text-primary" />
            Distribución Pareto — Top {Math.min(topN, productos.length)} de {formatNumber(productos.length)}
          </h6>
          <div className="d-flex align-items-center gap-2">
            <span style={{ fontSize: 12, color: "#64748b" }}>Mostrar:</span>
            {[20, 50, 100, 200].map(n => (
              <button key={n} type="button"
                className={`btn btn-sm ${topN === n ? "btn-primary" : "btn-outline-secondary"}`}
                style={{ padding: "2px 10px", fontSize: 12 }}
                onClick={() => setTopN(n)}
              >Top {n}</button>
            ))}
          </div>
        </div>

        <div style={{ width: "100%", height: 420 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={datosGrafico} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} interval={Math.floor(datosGrafico.length / 20)} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={v => formatCurrency(v).replace(",00", "")} />
              <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 11, fill: "#fbbf24" }} tickFormatter={v => `${v}%`} />
              <Tooltip content={<TooltipContent />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <ReferenceLine yAxisId="right" y={80} stroke="#10b981" strokeDasharray="5 5" label={{ value: "80% · Clase A", fill: "#10b981", fontSize: 10, position: "right" }} />
              <ReferenceLine yAxisId="right" y={95} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: "95% · Clase B", fill: "#f59e0b", fontSize: 10, position: "right" }} />
              <Bar yAxisId="left" dataKey="importe_neto" name="Importe">
                {datosGrafico.map((d, i) => <Cell key={i} fill={d.color || claseABC(d.clase).chartColor} />)}
              </Bar>
              <Line yAxisId="right" type="monotone" dataKey="aporte_acumulado_pct" name="% acumulado" stroke="#fbbf24" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div style={{ marginTop: 12, fontSize: 11, color: "#94a3b8", textAlign: "center" }}>
          Las barras muestran el importe neto por producto coloreadas por clase ABC. La línea amarilla
          muestra el porcentaje acumulado del catálogo.
        </div>
      </div>
    </div>
  )
}

ParetoTab.propTypes = { filtros: PropTypes.object }

export default ParetoTab
